import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { clubTheme } from "./ClubTheme";
import { FanAuthService, FanSession } from "./FanSession";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

/**
 * Sign in, or sign up.
 *
 * Both on one screen behind a toggle, because a supporter arriving here does
 * not know or care which they need — they know their email address, and half
 * of them genuinely can't remember whether they made an account last season.
 *
 * Registering with an address that already bought as a guest CLAIMS that
 * account rather than refusing: the server links the existing fan record, so
 * previous orders arrive with the login. That is the whole reason to sign up
 * at all, and it happens without anyone having to explain it.
 */
export function ShopAccount() {
	const { slug = "" } = useParams();
	const [search] = useSearchParams();
	const navigate = useNavigate();

	const [club, setClub] = React.useState<any>(null);
	const [mode, setMode] = React.useState<"in" | "up">(search.get("new") ? "up" : "in");
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [firstName, setFirstName] = React.useState("");
	const [lastName, setLastName] = React.useState("");
	const [marketing, setMarketing] = React.useState(false);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	// Where to go afterwards. Defaults to the tickets tab, but checkout sends
	// people here mid-purchase and they should land back on the basket.
	const next = search.get("next") || `/club/${slug}/tickets`;

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.get(slug);
				if (live && res.ok) setClub((await res.json()).club);
			} catch { /* plain header */ }
		})();
		return () => { live = false; };
	}, [slug]);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!email.trim() || !password) {
			setError("We need an email address and a password.");
			return;
		}
		if (mode === "up" && password.length < 8) {
			setError("Passwords need to be at least 8 characters.");
			return;
		}

		setBusy(true);
		try {
			const res = mode === "in"
				? await FanAuthService.login(email.trim(), password)
				: await FanAuthService.register({
					emailAddress: email.trim(),
					firstName: firstName.trim() || null,
					lastName: lastName.trim() || null,
					marketingConsent: marketing,
					password
				});

			if (!res.ok) {
				const body = await res.text();
				setBusy(false);
				// The server's message is the useful one — "an account already
				// exists", "invalid credentials" — so it is shown rather than
				// replaced with something vaguer.
				setError(extractMessage(body) || "That didn't work. Please check and try again.");
				return;
			}

			FanSession.set(await res.json());
			navigate(next);
		} catch {
			setBusy(false);
			setError("We couldn't reach the server. Please try again.");
		}
	};

	if (FanSession.signedIn()) {
		const profile = FanSession.profile();
		return (
			<div className="shop has-nav" style={clubTheme(club)}>
				<ShopHeader club={club} slug={slug} />
				<div className="shop-inner">
					<h2 className="shop-section-title">Your account</h2>
					<div className="shop-panel">
						<p style={{ margin: "0 0 6px" }}>
							Signed in as <strong>{profile?.emailAddress}</strong>
						</p>
						<p className="shop-card-meta" style={{ margin: 0 }}>
							Tickets you buy are saved to this account, on any device.
						</p>
					</div>
					<button className="shop-btn" onClick={() => navigate(`/club/${slug}/tickets`)} type="button">
						See your tickets
					</button>
					<button
						className="shop-btn secondary"
						onClick={() => { FanSession.clear(); navigate(`/club/${slug}`); }}
						style={{ marginTop: 12 }}
						type="button"
					>
						Sign out
					</button>
				</div>
				<ShopNav slug={slug} />
			</div>
		);
	}

	return (
		<div className="shop has-nav" style={clubTheme(club)}>
			<ShopHeader club={club} slug={slug} />
			<div className="shop-inner">
				<div className="shop-toggle">
					<button className={mode === "in" ? "on" : ""} onClick={() => setMode("in")} type="button">Sign in</button>
					<button className={mode === "up" ? "on" : ""} onClick={() => setMode("up")} type="button">Sign up</button>
				</div>

				<form onSubmit={submit}>
					<div className="shop-panel">
						{mode === "up" && <>
							<div className="shop-field">
								<label htmlFor="fan-first">First name</label>
								<input autoComplete="given-name" id="fan-first" onChange={(e) => setFirstName(e.target.value)} value={firstName} />
							</div>
							<div className="shop-field">
								<label htmlFor="fan-last">Last name</label>
								<input autoComplete="family-name" id="fan-last" onChange={(e) => setLastName(e.target.value)} value={lastName} />
							</div>
						</>}

						<div className="shop-field">
							<label htmlFor="fan-email">Email</label>
							<input autoComplete="email" id="fan-email" inputMode="email" onChange={(e) => setEmail(e.target.value)} type="email" value={email} />
						</div>

						<div className="shop-field">
							<label htmlFor="fan-password">Password</label>
							<input
								autoComplete={mode === "in" ? "current-password" : "new-password"}
								id="fan-password"
								onChange={(e) => setPassword(e.target.value)}
								type="password"
								value={password}
							/>
						</div>

						{mode === "up" &&
							<label className="shop-check">
								<input checked={marketing} onChange={(e) => setMarketing(e.target.checked)} type="checkbox" />
								<span>Email me about tickets and club news</span>
							</label>}
					</div>

					{error && <div className="shop-error">{error}</div>}

					<button className="shop-btn" disabled={busy} type="submit">
						{busy ? "Just a moment…" : mode === "in" ? "Sign in" : "Create account"}
					</button>
				</form>

				{/* Guest checkout stays available and stays visible. An account is
				    worth having, not a toll gate. */}
				<button className="shop-btn secondary" onClick={() => navigate(`/club/${slug}`)} style={{ marginTop: 12 }} type="button">
					Carry on without an account
				</button>

				{mode === "up" &&
					<p className="shop-note">
						Bought as a guest before? Sign up with the same email and those tickets come with you.
					</p>}
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}

/** The server sends a bare string or a JSON error; take whichever it is. */
function extractMessage(body: string): string | null {
	if (!body) return null;
	try {
		const parsed = JSON.parse(body);
		return parsed?.error || parsed?.message || parsed?.title || null;
	} catch {
		return body.replace(/^"|"$/g, "") || null;
	}
}
